import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Container,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { FiRefreshCw } from "react-icons/fi";
import { FaDownload } from "react-icons/fa";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getAssetAllocationReport } from "../../services/ReportServices";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import "react-datepicker/dist/react-datepicker.css";
import { FiBarChart2 } from "react-icons/fi";
import { BiSolidReport } from "react-icons/bi";

const allocationTypeOptions = [
  { value: "", label: "All Types" },
  { value: "EMPLOYEE", label: "Employee" },
  { value: "DEPARTMENT", label: "Department" },
];

export const periodOptions = [
  { label: "All", value: "all" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "7_days" },
  { label: "Last 15 Days", value: "15_days" },
  { label: "Last 30 Days", value: "30_days" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "3_months" },
  { label: "Custom Date", value: "custom" },
];

const getTypeColor = (type) => {
  const colors = {
    EMPLOYEE: "primary",
    DEPARTMENT: "success",
    AREA: "info",
  };
  return colors[type] || "secondary";
};

const AssetAllocationReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[4]); // Default to "Last Month"

  const [filters, setFilters] = useState({
    employee: "",
    department: "",
    area: "",
    allocation_type: "",
    search: "",
    page: 1,
    page_size: 10,
    period: "all",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    const today = new Date();
    let startDate = "";
    let endDate = "";

    switch (selectedPeriod.value) {
      case "yesterday":
        startDate = format(subDays(today, 1), "yyyy-MM-dd");
        endDate = format(subDays(today, 1), "yyyy-MM-dd");
        break;
      case "7_days":
        startDate = format(subDays(today, 6), "yyyy-MM-dd");
        endDate = format(today, "yyyy-MM-dd");
        break;
      case "15_days":
        startDate = format(subDays(today, 14), "yyyy-MM-dd");
        endDate = format(today, "yyyy-MM-dd");
        break;
      case "30_days":
        startDate = format(subDays(today, 29), "yyyy-MM-dd");
        endDate = format(today, "yyyy-MM-dd");
        break;
      case "last_month":
        const firstDayLastMonth = startOfMonth(subMonths(today, 1));
        const lastDayLastMonth = endOfMonth(subMonths(today, 1));
        startDate = format(firstDayLastMonth, "yyyy-MM-dd");
        endDate = format(lastDayLastMonth, "yyyy-MM-dd");
        break;
      case "3_months":
        startDate = format(subMonths(today, 3), "yyyy-MM-dd");
        endDate = format(today, "yyyy-MM-dd");
        break;
      case "custom":
        // Don't set dates for custom - they'll be picked by the date picker
        break;
      default: // "all"
        // No dates needed
        break;
    }

    setFilters((prev) => ({
      ...prev,
      period: selectedPeriod.value,
      start_date: startDate,
      end_date: endDate,
      page: 1,
    }));
  }, [selectedPeriod]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (
        filters.period === "custom" &&
        (!filters.start_date || !filters.end_date)
      ) {
        setReportData([]);
        setTotalCount(0);
        return;
      }

      const params = {
        ...filters,
        employee: filters.employee || undefined,
        department: filters.department || undefined,
        area: filters.area || undefined,
        allocation_type: filters.allocation_type || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        search: filters.search || undefined,
        page: filters.page,
        page_size: filters.page_size,
      };

      const response = await getAssetAllocationReport(params);
      setReportData(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      const message = err.message || "Failed to load report";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await getAssetAllocationReport(
        { ...filters, export: true },
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "asset_allocation_report.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Exported successfully");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (selectedPage) => {
    setFilters((prev) => ({ ...prev, page: selectedPage.selected + 1 }));
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const delay = setTimeout(fetchData, 300);
    return () => clearTimeout(delay);
  }, [
    filters.page,
    filters.page_size,
    filters.search,
    filters.employee,
    filters.department,
    filters.area,
    filters.allocation_type,
    filters.start_date,
    filters.end_date,
  ]);

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="text-primary mb-0">Asset Allocation Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Asset Allocation Report</li>
          </ol>
        </div>

        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: "250px" }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FaDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card style={{ minHeight: "350px" }}>
        <Card.Header className="d-flex justify-content-between flex-wrap gap-2 align-items-center">
          <div className="d-flex align-items-center gap-2 text-primary fw-bold fs-5 mb-0">
            <BiSolidReport size={22} color="#255d5c" />
            <h5 className="mb-0 fw-bold text-dark">Asset Allocation Report</h5>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={filters.search}
              handleSearchChange={(value) => handleChange("search", value)}
              placeholder="Search allocations..."
            />

            <GlobalSearchableDropdown
              options={allocationTypeOptions}
              selected={allocationTypeOptions.find(
                (opt) => opt.value === filters.allocation_type
              )}
              onSelect={(selected) =>
                handleChange("allocation_type", selected?.value || "")
              }
              placeholder="Allocation Type"
              width="180px"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            <GlobalSearchableDropdown
              options={periodOptions}
              selected={selectedPeriod}
              onSelect={(option) => {
                setSelectedPeriod(option || periodOptions[0]); // Reset to "All" on clear
              }}
              placeholder="Period"
              width="150px"
              isClearable={selectedPeriod.value !== "all"} // <-- Only clearable if not "All"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            {selectedPeriod?.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  endDate={filters.end_date ? new Date(filters.end_date) : null}
                  onChange={([start, end]) => {
                    setFilters((prev) => ({
                      ...prev,
                      start_date: start ? format(start, "yyyy-MM-dd") : "",
                      end_date: end ? format(end, "yyyy-MM-dd") : "",
                      period: "custom",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}{" "}
              <Button variant="link" size="sm" onClick={fetchData}>
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table
                responsive
                size="sm"
                className="align-middle text-center mb-0 table-sm"
              >
                <thead className="table-light">
                  <tr>
                    <th>Asset</th>
                    <th>Allocated To</th>
                    <th>Type</th>
                    <th>Allocated By</th>
                    <th>Location</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-2 ">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    reportData.map((item, index) => (
                      <tr key={index} className="py-1">
                        <td className="text-start py-1 px-2">
                          <div className="fw-semibold small mb-0">
                            {item.asset_number}
                          </div>
                          <div className=" small">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            {item.asset_name}
                          </div>
                        </td>
                        <td className="py-1 px-2">
                          <div className="small">{item.allocated_to}</div>
                        </td>
                        <td className="py-1 px-2">
                          <span
                            className={`badge bg-${getTypeColor(
                              item.allocated_type
                            )} text-white small`}
                            style={{
                              fontSize: "0.65rem",
                              padding: "0.3em 0.5em",
                            }}
                          >
                            {item.allocated_type}
                          </span>
                        </td>

                        <td className="py-1 px-2">
                          <div className="small">{item.allocated_by}</div>
                        </td>
                        <td className="py-1 px-2">
                          <div className="small">{item.location}</div>
                        </td>
                        <td className="py-1 px-2">
                          <div className="small">{item.allocated_at}</div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              <div className="d-flex justify-content-end mt-3">
                <Pagination
                  currentPage={filters.page - 0}
                  recordsPage={filters.page_size}
                  dataLength={totalCount}
                  handlePagination={handlePageChange}
                  showPageInfo={true}
                />
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AssetAllocationReport;
